import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, MessageCircle, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Logo } from "@/components/Logo";
import { site, whatsappHref } from "@/config/site";
import { useAssessment } from "@/context/AssessmentContext";

const socialIcons = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, youtube: Youtube };

export default function Footer() {
  const { openAssessment } = useAssessment();
  const wa = whatsappHref();
  const activeSocials = Object.entries(site.social).filter(([, url]) => url);

  return (
    <footer className="relative overflow-hidden bg-navy text-white" data-testid="site-footer">
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-ocean/20 blur-3xl" />
      <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-sky/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Logo variant="light" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/70">
              Hospitality consultancy and digital growth solutions for hotels, guest houses, resorts and property owners.
            </p>
            <p className="mt-4 text-xs text-white/50">Serving hospitality businesses across Pakistan.</p>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white">Company</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li><Link to="/about" className="transition-colors hover:text-sky" data-testid="footer-about">About</Link></li>
              <li><Link to="/services" className="transition-colors hover:text-sky" data-testid="footer-services">Services</Link></li>
              <li><Link to="/packages" className="transition-colors hover:text-sky" data-testid="footer-packages">Packages</Link></li>
              <li><Link to="/process" className="transition-colors hover:text-sky" data-testid="footer-process">Our Process</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white">For Property Owners</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li><Link to="/list-your-property" className="transition-colors hover:text-sky" data-testid="footer-list-property">List Your Property</Link></li>
              <li><button onClick={openAssessment} className="transition-colors hover:text-sky" data-testid="footer-assessment">Free Property Assessment</button></li>
              <li><Link to="/packages" className="transition-colors hover:text-sky" data-testid="footer-custom-package">Custom Package</Link></li>
              <li><Link to="/contact" className="transition-colors hover:text-sky" data-testid="footer-contact">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-lg text-white">Contact</h4>
            <ul className="mt-5 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-sky" />
                <a href={`mailto:${site.email}`} className="break-all transition-colors hover:text-sky" data-testid="footer-email">{site.email}</a>
              </li>
              {site.phone && (
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 shrink-0 text-sky" />
                  <a href={`tel:${site.phone.replace(/\s+/g, "")}`} className="transition-colors hover:text-sky" data-testid="footer-phone">{site.phone}</a>
                </li>
              )}
              {wa && (
                <li className="flex items-center gap-3">
                  <MessageCircle className="h-4 w-4 shrink-0 text-sky" />
                  <a href={wa} target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-sky" data-testid="footer-whatsapp">WhatsApp</a>
                </li>
              )}
              <li className="flex items-center gap-3">
                <MapPin className="h-4 w-4 shrink-0 text-sky" />
                <span>{site.location}</span>
              </li>
            </ul>

            {activeSocials.length > 0 && (
              <div className="mt-6 flex gap-3">
                {activeSocials.map(([key, url]) => {
                  const Icon = socialIcons[key];
                  if (!Icon) return null;
                  return (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-sky hover:text-navy" data-testid={`footer-social-${key}`}>
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-white/50 sm:flex-row">
          <p>© 2026 HM Services. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="transition-colors hover:text-sky" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="transition-colors hover:text-sky" data-testid="footer-terms">Terms &amp; Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
